// Phase 16.5 — Admin data cleanup page (quarantine fake/test/seed data)
//
// Flow:
//   1. Click "Dry run" → server returns matched records per collection
//   2. Review the breakdown (no writes happen)
//   3. Click "Apply (quarantine)" → server sets is_test=true everywhere
//      so dashboards filter them out. Master admin + real affiliates are
//      automatically protected server-side.
//   4. History table shows past cleanup runs.

import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Loader2, Database, History } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export default function AdminDataCleanup() {
  const [dryRun, setDryRun]       = useState(null);
  const [busyDry, setBusyDry]     = useState(false);
  const [busyApply, setBusyApply] = useState(false);
  const [reports, setReports]     = useState(null);
  const [err, setErr]             = useState("");
  const [confirmText, setConfirm] = useState("");

  const loadReports = async () => {
    try {
      const { data } = await api.get("/admin/data-cleanup/report");
      setReports(data.reports || []);
    } catch (_) { /* non-fatal */ }
  };
  useEffect(() => { loadReports(); }, []);

  const runDry = async () => {
    setBusyDry(true); setErr("");
    try {
      const { data } = await api.post("/admin/data-cleanup/dry-run", {});
      setDryRun(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusyDry(false); }
  };
  const applyCleanup = async () => {
    if (confirmText !== "QUARANTINE") { setErr("Type QUARANTINE to confirm."); return; }
    setBusyApply(true); setErr("");
    try {
      await api.post("/admin/data-cleanup/apply", { quarantine_only: true });
      setConfirm("");
      setDryRun(null);
      await loadReports();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusyApply(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="admin-data-cleanup">
      <header>
        <div className="eyebrow">Admin · Operations</div>
        <h1 className="font-anton text-3xl uppercase">Data cleanup</h1>
        <p className="text-xs text-lifted-dim mt-1 max-w-2xl">
          Quarantine fake / test / seed records so live dashboards show only
          real activity. Master admin + real affiliates are protected automatically.
          Quarantined records are NOT deleted — they're flagged{" "}
          <code className="bg-black/30 px-1 py-0.5 rounded">is_test: true</code> so
          analytics filter them out. Reversible by clearing the flag in Mongo.
        </p>
      </header>

      <section className="glass-panel p-5" data-testid="dc-dry-run-section">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-lifted-green" />
            <h2 className="font-bebas text-sm tracking-[0.22em] uppercase">Dry run</h2>
          </div>
          <button onClick={runDry} disabled={busyDry}
                  className="btn-lifted text-xs disabled:opacity-50 inline-flex items-center gap-1"
                  data-testid="dc-run-dry">
            {busyDry ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {busyDry ? "Scanning…" : "Run scan"}
          </button>
        </div>

        {err && <div className="text-rose-300 text-xs mb-3" data-testid="dc-error">{err}</div>}

        {dryRun ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              <Stat label="Total test docs"        value={dryRun.total_test_docs} accent />
              <Stat label="Protected accounts"     value={dryRun.protected_accounts_visible} />
              <Stat label="Collections scanned"    value={(dryRun.collections || []).length} />
              <Stat label="Rules applied"          value={(dryRun.rules_applied || []).length} />
            </div>
            <table className="w-full text-xs" data-testid="dc-collections-table">
              <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
                <tr className="border-b border-white/5">
                  <th className="px-2 py-2 text-left">Collection</th>
                  <th className="px-2 py-2 text-right">Test docs</th>
                  <th className="px-2 py-2 text-left">Sample rows</th>
                </tr>
              </thead>
              <tbody>
                {(dryRun.collections || []).map((c) => (
                  <tr key={c.collection} className="border-b border-white/5"
                      data-testid={`dc-row-${c.collection}`}>
                    <td className="px-2 py-2 font-mono">{c.collection}</td>
                    <td className={`px-2 py-2 text-right ${c.count > 0 ? "text-amber-300" : "text-lifted-dim"}`}>
                      {c.count}
                    </td>
                    <td className="px-2 py-2 text-lifted-dim">
                      {(c.sample || []).map((s, i) => (
                        <div key={i} className="truncate">
                          {s.email || s.title || s.id} {s.source && `· ${s.source}`}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {dryRun.total_test_docs > 0 && (
              <div className="mt-5 border-t border-white/10 pt-4">
                <div className="flex items-center gap-2 text-amber-300 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  <h3 className="font-bebas text-sm tracking-[0.22em] uppercase">
                    Apply quarantine
                  </h3>
                </div>
                <p className="text-xs text-lifted-dim mb-3">
                  This will flag <span className="text-amber-300 font-mono">{dryRun.total_test_docs}</span>
                  {" "}records as <code>is_test: true</code> so they disappear from live dashboards.
                  Master admin + real users are NOT touched. Type{" "}
                  <code className="bg-black/30 px-1 py-0.5 rounded">QUARANTINE</code>{" "}
                  to confirm.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text" value={confirmText}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Type QUARANTINE"
                    className="input-lifted text-xs"
                    data-testid="dc-confirm-text"
                  />
                  <button onClick={applyCleanup}
                          disabled={busyApply || confirmText !== "QUARANTINE"}
                          className="btn-lifted text-xs disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid="dc-apply">
                    {busyApply ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                    {busyApply ? "Quarantining…" : "Apply quarantine"}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-lifted-dim">
            Click <strong className="text-white">Run scan</strong> to see what would be quarantined.
            Nothing is written until you confirm.
          </div>
        )}
      </section>

      <section className="glass-panel p-5" data-testid="dc-history-section">
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-lifted-green" />
          <h2 className="font-bebas text-sm tracking-[0.22em] uppercase">Cleanup history</h2>
        </div>
        {!reports?.length ? (
          <div className="text-xs text-lifted-dim">No cleanups applied yet.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
              <tr className="border-b border-white/5">
                <th className="px-2 py-2 text-left">When</th>
                <th className="px-2 py-2 text-right">Quarantined</th>
                <th className="px-2 py-2 text-right">Deleted</th>
                <th className="px-2 py-2 text-left">Mode</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-b border-white/5">
                  <td className="px-2 py-2">{(r.created_at || "").slice(0, 19)}</td>
                  <td className="px-2 py-2 text-right text-amber-300">{r.total_quarantined}</td>
                  <td className="px-2 py-2 text-right text-rose-300">{r.total_deleted}</td>
                  <td className="px-2 py-2 text-lifted-dim">
                    {r.quarantine_only ? "quarantine" : "delete"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Iter68 — Seed account archive (live-production-safe) */}
      <Iter68SeedCleanup />

      {/* Iter71 — Nuclear partner/driver/franchise reset */}
      <Iter71NuclearReset />
    </div>
  );
}

function Iter68SeedCleanup() {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [applied, setApplied] = React.useState(null);

  const runDry = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/cleanup/seed-data?dry_run=true", {});
      setPreview(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (confirm !== "ARCHIVE SEEDS") { setErr("Type ARCHIVE SEEDS to confirm."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/cleanup/seed-data?dry_run=false", {});
      setApplied(data);
      setPreview(null);
      setConfirm("");
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="glass-panel p-5 space-y-4" data-testid="iter68-seed-cleanup">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-lifted-green" />
        <h2 className="font-anton uppercase text-xl text-white">Iter68 — Archive Seed/Demo Accounts</h2>
      </div>
      <p className="text-sm text-lifted-dim max-w-3xl">
        Soft-archives accounts whose email matches <code>*@example.com</code> OR name matches "Test/Fail/Doomed/Phase4/Attach Partner/Driver-zone/Partner Tester/P2/P114".
        Production keep-list and protected affiliate slugs are never touched.
        Master admin accounts are always preserved.
      </p>

      {err && <div className="text-rose-300 text-sm">{err}</div>}

      <div className="flex flex-wrap gap-2">
        <button
          data-testid="iter68-cleanup-dry-run"
          onClick={runDry}
          disabled={busy}
          className="btn-ghost !text-xs !py-2 !px-3"
        >{busy ? "Working…" : "Dry run (preview)"}</button>
      </div>

      {preview && (
        <div className="border border-amber-400/30 bg-amber-500/5 rounded-md p-4 space-y-3">
          <div className="text-sm text-amber-200">
            Would archive:
            <strong className="text-white"> {preview.counts.users} users</strong>,
            <strong className="text-white"> {preview.counts.partners} partners</strong>,
            <strong className="text-white"> {preview.counts.franchises} franchises</strong>,
            <strong className="text-white"> {preview.counts.drivers} drivers</strong>,
            <strong className="text-white"> {preview.counts.affiliates} affiliates</strong>.
          </div>
          {preview.sample_emails?.length > 0 && (
            <details className="text-xs text-lifted-dim">
              <summary className="cursor-pointer">Sample emails ({preview.sample_emails.length})</summary>
              <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto">
                {preview.sample_emails.map((e) => <li key={e} className="font-mono">{e}</li>)}
              </ul>
            </details>
          )}
          <div className="flex items-center gap-2">
            <input
              data-testid="iter68-cleanup-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder='Type "ARCHIVE SEEDS" to confirm'
              className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-400/40"
            />
            <button
              data-testid="iter68-cleanup-apply"
              onClick={apply}
              disabled={busy || confirm !== "ARCHIVE SEEDS"}
              className="btn-lifted !text-xs !py-2 !px-3 disabled:opacity-40"
            >{busy ? "Archiving…" : "Apply"}</button>
          </div>
        </div>
      )}

      {applied && (
        <div className="border border-lifted-green/30 bg-lifted-green/5 rounded-md p-4 text-sm text-lifted-green" data-testid="iter68-cleanup-applied">
          ✅ Archived {applied.counts.users} users, {applied.counts.partners} partners,
          {' '}{applied.counts.franchises} franchises, {applied.counts.drivers} drivers,
          {' '}{applied.counts.affiliates} affiliates. All actions are reversible by
          flipping status back via /admin/users.
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="glass-panel p-3 border border-white/10">
      <div className="text-[0.6rem] tracking-[0.18em] uppercase font-bebas text-lifted-dim">{label}</div>
      <div className={`font-anton text-2xl ${accent ? "text-amber-300" : "text-white"}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}


function Iter71NuclearReset() {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [applied, setApplied] = React.useState(null);

  const runDry = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/cleanup/reset-active-partners-drivers-franchises?dry_run=true", {});
      setPreview(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (confirm !== "RESET ECOSYSTEM") { setErr("Type RESET ECOSYSTEM to confirm."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/cleanup/reset-active-partners-drivers-franchises?dry_run=false", {});
      setApplied(data); setPreview(null); setConfirm("");
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="glass-panel p-5 space-y-4 border border-rose-400/30" data-testid="iter71-nuclear-reset">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-rose-300" />
        <h2 className="font-anton uppercase text-xl text-white">Iter71 — Reset ALL Active Partners / Drivers / Franchises</h2>
      </div>
      <p className="text-sm text-lifted-dim max-w-3xl">
        Soft-archives <strong>every</strong> partner_profile, franchise_profile, and driver_profile
        on the platform and demotes those users (removing partner/driver/franchisee roles, keeping customer).
        Use this when you want the ecosystem back to zero active partners/drivers/franchises before re-onboarding real ones.
        Master admin keep-list is NEVER touched (Brian, Jesse Workman, Mike Rose, etc.).
        Fully reversible — flip status back to restore.
      </p>

      {err && <div className="text-rose-300 text-sm">{err}</div>}

      <button
        data-testid="iter71-reset-dry-run"
        onClick={runDry}
        disabled={busy}
        className="btn-ghost !text-xs !py-2 !px-3"
      >{busy ? "Working…" : "Dry run (preview)"}</button>

      {preview && (
        <div className="border border-rose-400/30 bg-rose-500/5 rounded-md p-4 space-y-3">
          <div className="text-sm text-rose-200 leading-relaxed">
            Would archive:
            <ul className="list-disc pl-5 mt-1 space-y-0.5 text-white">
              <li><strong>{preview.counts.partner_profiles}</strong> partner stores</li>
              <li><strong>{preview.counts.franchise_profiles}</strong> franchises</li>
              <li><strong>{preview.counts.driver_profiles}</strong> driver profiles</li>
              <li>Demote <strong>{preview.counts.partner_users}</strong> partner users, <strong>{preview.counts.driver_users}</strong> driver users, <strong>{preview.counts.franchise_users}</strong> franchise users (master-admin keep-list preserved).</li>
            </ul>
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="iter71-reset-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder='Type "RESET ECOSYSTEM" to confirm'
              className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-rose-400/40"
            />
            <button
              data-testid="iter71-reset-apply"
              onClick={apply}
              disabled={busy || confirm !== "RESET ECOSYSTEM"}
              className="btn-lifted !text-xs !py-2 !px-3 disabled:opacity-40 bg-rose-500/80 hover:bg-rose-500"
            >{busy ? "Archiving…" : "Apply"}</button>
          </div>
        </div>
      )}

      {applied && (
        <div className="border border-lifted-green/30 bg-lifted-green/5 rounded-md p-4 text-sm text-lifted-green" data-testid="iter71-reset-applied">
          ✅ Archived {applied.archived.partner_profiles} partner stores,
          {' '}{applied.archived.franchise_profiles} franchises,
          {' '}{applied.archived.driver_profiles} drivers.
          Demoted {applied.demoted_users.partner + applied.demoted_users.driver + applied.demoted_users.franchisee} users.
          Kept {applied.kept_users_count} protected production users.
        </div>
      )}
    </section>
  );
}
